import { useState, useEffect } from 'react';
import { httpService } from '../services';
import { createLogger } from '../utils/logger';

const logger = createLogger('useAuthenticatedImage');

interface UseAuthenticatedImageResult {
    src: string | null;
    loading: boolean;
    error: string | null;
}

/**
 * Hook to load authenticated image via axios (with automatic token refresh on 403)
 * Converts the blob response to an object URL for use in <img src={...}>
 * @param url - API URL to fetch (e.g. /photos/{id}/stream)
 * @returns { src, loading, error } where src is a blob object URL or null
 */
export const useAuthenticatedImage = (url: string | null): UseAuthenticatedImageResult => {
    const [src, setSrc] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!url) {
            setSrc(null);
            setError(null);
            return;
        }

        let mounted = true;

        const fetchImage = async () => {
            try {
                setLoading(true);
                setError(null);
                
                // Fetch via axios which has built-in 403 → refreshLogin() → retry logic
                const response = await httpService.get(url, {
                    responseType: 'blob'
                });

                if (!mounted) return;

                // Convert blob to object URL
                const blob = response.data as Blob;
                const objectUrl = URL.createObjectURL(blob);
                setSrc(objectUrl);
            } catch (err) {
                if (!mounted) return;
                
                const message = err instanceof Error ? err.message : 'Failed to load image';
                logger.error('Failed to fetch', url, ':', err);
                setError(message);
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        fetchImage();

        // Cleanup: revoke object URL on unmount or URL change
        return () => {
            mounted = false;
            if (src) {
                URL.revokeObjectURL(src);
            }
        };
    }, [url]);

    return { src, loading, error };
};
